import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, InputAdornment, Stack } from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import * as PATHS from 'js/constants/pathnames';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import { Table } from 'new/elements/Table';
import { TextField } from 'new/elements/TextField';
import { ScreenComponent } from 'new/routes/RouterManager';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useDistance } from 'new/hooks/useDistance';
import { slugify } from 'new/utils/slugify';
import UserPage from 'new/components/settings/userPage/UserPage';
import { AddUser } from 'new/components/settings/Members/AddUser';
import { useFetchAdminOrgs, useFetchAdminOrgTiers } from 'new/stores/adminOrgs/adminOrgs.api';
import { AdminOrg } from 'new/stores/adminOrgs/adminOrgs.types';
import { buildColumns } from './columns';
import { GuardedControl } from './GuardedControl';
import { PopularFilters } from './PopularFilters';
import {
  buildOrgSearchText,
  getOrgFilterStatus,
  orgMatchesPlanFilter,
  StatusFilterValue,
} from './utils';
import { CustomerOrgDrawer } from './drawer/CustomerOrgDrawer';
import { CreateOrgSheet } from './CreateOrgSheet';
import styles from './CustomerOrgs.module.scss';

interface CustomerOrgsProps {}

export const CustomerOrgs: ScreenComponent<CustomerOrgsProps> = () => {
  const pageRef = useRef<HTMLDivElement>();
  const history = useHistory();
  const user = useCurrentUser();
  const distance = useDistance(pageRef, 400);

  // Defaults match what admins care about day-to-day: paying, active customers.
  const [planFilter, setPlanFilter] = useState<string>('PAID');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('ACTIVE');
  const [search, setSearch] = useState('');
  const [openOrgId, setOpenOrgId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Always starts locked, even for a super_user, on every mount. The toggle itself now
  // lives inside CustomerOrgDrawer (see RoleBanner) so it's visible in the context where
  // an admin is actually about to make a change — this list page only reads the state.
  const [writeArmed, setWriteArmed] = useState(false);

  const { data: orgs = [], isFetching } = useFetchAdminOrgs();
  const { data: tiers = [] } = useFetchAdminOrgTiers();

  const canWrite = !!user?.super_user;

  useEffect(() => {
    if (!user?.super_user || user?.impersonated) {
      history.replace(UserPage.screenConfig.path);
    }
  }, [user?.super_user, user?.impersonated, history]);

  const filteredData = useMemo(() => {
    const query = slugify(search.trim().toLowerCase());
    return orgs
      .filter((org) => orgMatchesPlanFilter(org, planFilter))
      .filter((org) => statusFilter === 'ALL' || getOrgFilterStatus(org) === statusFilter)
      .filter((org) => !query || slugify(buildOrgSearchText(org)).includes(query));
  }, [orgs, planFilter, statusFilter, search]);

  const columns = useMemo(
    () =>
      buildColumns({
        tiers,
        planFilter,
        onPlanFilterChange: setPlanFilter,
        statusFilter,
        onStatusFilterChange: setStatusFilter,
      }),
    [tiers, planFilter, statusFilter],
  );

  const onPopularFilterChange = (status: StatusFilterValue, plan: string) => {
    setStatusFilter(status);
    setPlanFilter(plan);
  };

  const onRowClick = (org: AdminOrg) => setOpenOrgId(org.id);

  return (
    <PageLayout
      ref={pageRef}
      title="Customer Organizations"
      className={styles.container}
      description="Manage plans, quotas, trials, and lifecycle for Nexla customer orgs."
      rightView={
        // Gated on real permission only, not the writeArmed speed bump — that toggle now
        // lives inside the org drawer (see RoleBanner), out of view from this list page,
        // and Create Org's own sheet already has its own explicit submit step.
        <GuardedControl disabled={!canWrite}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Create org
          </Button>
        </GuardedControl>
      }
    >
      <Stack className={styles.toolbar} direction="row">
        <PopularFilters
          orgs={orgs}
          statusFilter={statusFilter}
          planFilter={planFilter}
          onChange={onPopularFilterChange}
        />
        <div className={styles.toolbarFilters}>
          <TextField
            className={styles.search}
            placeholder="Search name, domain, plan, status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
      </Stack>

      <Table
        rows={filteredData}
        loading={isFetching}
        height={distance}
        columns={columns}
        headClassName={styles.tableHeadTopAligned}
        onRowClick={onRowClick}
        keyExtractor={(org) => org.id}
        emptyStateText="No orgs match your filters."
      />

      <CustomerOrgDrawer
        orgId={openOrgId}
        canWrite={canWrite}
        writeArmed={writeArmed}
        onToggleWriteArmed={setWriteArmed}
        onClose={() => setOpenOrgId(null)}
      />
      <CreateOrgSheet open={createOpen} onClose={() => setCreateOpen(false)} />
    </PageLayout>
  );
};

CustomerOrgs.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/customer-orgs`,
  screenName: 'settings.CustomerOrgs',
  pageTitle: 'Customer Orgs',
  header: {
    title: 'Settings',
    slugTitle: 'Customer Orgs',
  },
  domain: 'settings',
  modals: [AddUser],
};
